import React, { useId } from 'react'
import { createPortal } from 'react-dom'
import {
  MVM_MODAL_BG_URL,
  MVM_MODAL_BATTLE_REWARDS_BG_URL,
  MVM_MODAL_BORDER_URL,
  MVM_MODAL_DEFEAT_BACK_BTN_URL,
  MVM_MODAL_DEFEAT_LOGO_BG_URL,
  MVM_MODAL_LOOT_CARD_BG_URL,
  MVM_MODAL_VICTORY_BACK_BTN_URL,
  MVM_MODAL_VICTORY_LOGO_BG_URL,
} from './mvmModalAssets'
import { formatCompactResourceAmount } from './mvmBattleResultUi'
import { MVM_BATTLE_UI_FONT_FAMILY } from '../../pages/hex-battle/constants'

/** Logo strip height (dynamic viewport — tracks mobile browser chrome). */
const LOGO_BLOCK_HEIGHT_DVH = 30
/** Vertical offset — moves the logo strip down inside the stack. */
const LOGO_TOP_OFFSET_DVH = 3
/**
 * Share of the logo block height that overlaps the modal body (bottom of logo over parchment).
 * Modal top = `LOGO_TOP_OFFSET_DVH + (1 - ratio) * LOGO_BLOCK_HEIGHT_DVH` in dvh.
 */
const LOGO_MODAL_OVERLAP_RATIO = 0.3
const MODAL_PANEL_MARGIN_TOP = `calc(${LOGO_TOP_OFFSET_DVH + LOGO_BLOCK_HEIGHT_DVH * (1 - LOGO_MODAL_OVERLAP_RATIO)}dvh)`

/**
 * Responsive sizing: vertical rhythm tracks viewport height with `dvh` so short screens
 * (and mobile browser chrome) shrink the whole modal together. Clamp mins use `dvh` instead
 * of fixed pixels so parts keep scaling down instead of hitting a pixel floor.
 */
export const MVM_RESULT_MODAL_RV = {
  modalMaxW: 'min(92vw, 980px, 118dvh)',
  overlayPad:
    'clamp(1.2dvh, min(2.5dvh, 28px), 28px) clamp(12px, 3.5vw, 40px) clamp(1.4dvh, 3dvh, 40px)',
  bodyPad: 'clamp(1.4dvh, 4dvh, 64px) clamp(16px, 8vw, 120px) clamp(1.4dvh, 4.5dvh, 72px)',
  logoMinH: 'clamp(12dvh, 30dvh, 240px)',
  logoInnerPad: 'clamp(1.1dvh, 1.8dvh, 22px) clamp(14px, 3vw, 32px) clamp(1.1dvh, 2dvh, 24px)',
  battleRewardsPadX: 'clamp(16px, 6vw, 96px)',
  battleRewardsGap: 'clamp(1.1dvh, 2dvh, 48px)',
  sectionTitleFont: 'clamp(1.15dvh, 1.35dvh, 14px)',
  sectionTitleMargin: 'clamp(0.45dvh, 0.8dvh, 10px) 0 clamp(0.65dvh, 1dvh, 12px)',
  descMarginBottom: 'clamp(0.9dvh, 1.2dvh, 16px)',
  borderMarginBottom: 'clamp(0.9dvh, 1.2dvh, 14px)',
  blockMarginBottom: 'clamp(1.1dvh, 1.4dvh, 18px)',
  glyphBox: 'clamp(2.6dvh, 3dvh, 34px)',
  xpCardMinH: 'clamp(5.2dvh, 7dvh, 68px)',
  xpCardPad: 'clamp(0.75dvh, 1.1dvh, 14px) clamp(8px, 1.2vw, 14px)',
  xpCardGap: 'clamp(0.65dvh, 1dvh, 12px)',
  xpLabelFont: 'clamp(1.1dvh, 1.25dvh, 13px)',
  xpAmountFont: 'clamp(1.2dvh, 1.45dvh, 16px)',
  lootRowGap: 'clamp(0.75dvh, 1dvh, 14px)',
  lootPadBottom: 'clamp(0.45dvh, 0.7dvh, 10px)',
  lootCardMinH: 'clamp(9.5dvh, 12dvh, 142px)',
  /** Card width: modest cap + wrapped row keeps many resources inside the parchment. */
  lootCardW: 'min(9vw, 10.5dvh, 92px)',
  lootCardMinW: 'min(7.8vw, 8.8dvh, 64px)',
  lootCardPad: 'clamp(0.55dvh, 0.85dvh, 11px) clamp(5px, 0.95vw, 10px) clamp(0.75dvh, 1.05dvh, 13px)',
  lootIconBox: 'clamp(2.6dvh, 3.5dvh, 34px)',
  lootIconImg: 'clamp(2.4dvh, 3.1dvh, 30px)',
  lootNameFont: 'clamp(1dvh, 1.15dvh, 12px)',
  lootAmtFont: 'clamp(1.2dvh, 1.5dvh, 16px)',
  lootRuleMargin: 'clamp(0.45dvh, 0.7dvh, 8px) 0',
  lootFallbackGlyph: 'clamp(1.85dvh, 2.2dvh, 24px)',
  emptyLootFont: 'clamp(1.15dvh, 1.35dvh, 13px)',
  emptyLootMargin: '0 0 clamp(1.1dvh, 1.4dvh, 18px)',
  backBtnMaxW: 'min(36vw, 28dvh, 320px)',
  backBtnMinH: 'clamp(4.8dvh, 6.5dvh, 58px)',
  backBtnPad: 'clamp(0.75dvh, 1.2dvh, 14px) clamp(12px, 2.5vw, 24px)',
  backBtnFont: 'clamp(1.35dvh, 1.6dvh, 16px)',
  backBtnMarginTop: 'clamp(0.65dvh, 1dvh, 12px)',
  /** Logo title: caps huge screens, still tracks height on short viewports. */
  logoTitleFont: 'clamp(3.8dvh, 7dvh, 56px)',
  logoTitleMarginTop: 'clamp(4dvh, 9dvh, 72px)',
  /** Space under logo overlap into parchment — scales with height. */
  descriptionTopMargin: 'clamp(2dvh, 5dvh, 48px)',
} as const

const RV = MVM_RESULT_MODAL_RV

const TEXT_PRIMARY = 'rgba(228,220,200,.95)'
const TEXT_SECTION = 'rgba(228,220,200,.88)'

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
  fontSize: RV.sectionTitleFont,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: TEXT_SECTION,
  margin: RV.sectionTitleMargin,
  textAlign: 'center',
}

function XpGlyph({ uid, variant }: { uid: string; variant: 'character' | 'account' }) {
  const gid = `${variant}-${uid}`
  const s = RV.glyphBox
  if (variant === 'character') {
    return (
      <svg width={s} height={s} viewBox="0 0 28 28" aria-hidden>
        <defs>
          <linearGradient id={`${gid}-g`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#d8ffd8" />
            <stop offset="100%" stopColor="#2e7d32" />
          </linearGradient>
        </defs>
        <circle cx="14" cy="14" r="12" fill="rgba(0,0,0,.28)" />
        <path
          d="M14 3.5l2.4 7.4h7.8L17.8 16l2.4 7.4L14 18.8 7.8 23.4 10.2 16 3.8 10.9h7.8z"
          fill={`url(#${gid}-g)`}
        />
      </svg>
    )
  }
  return (
    <svg width={s} height={s} viewBox="0 0 28 28" aria-hidden>
      <defs>
        <linearGradient id={`${gid}-g`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffe9a8" />
          <stop offset="100%" stopColor="#b8860b" />
        </linearGradient>
      </defs>
      <circle cx="14" cy="14" r="12" fill="rgba(0,0,0,.28)" />
      <path
        d="M14 4L6 8v6c0 4.5 3.4 8.7 8 10 4.6-1.3 8-5.5 8-10V8l-8-4zm0 3.2L18.5 9v4.5c0 2.9-2 5.6-4.5 6.4-2.5-.8-4.5-3.5-4.5-6.4V9L14 7.2z"
        fill={`url(#${gid}-g)`}
      />
    </svg>
  )
}

function MvMXpRewardCard({
  uid,
  variant,
  label,
  amount,
  sign,
}: {
  uid: string
  variant: 'character' | 'account'
  label: string
  amount: number
  sign: '+' | '−' | '-'
}) {
  const signCh = sign === '−' || sign === '-' ? '−' : '+'
  const formatted = formatCompactResourceAmount(amount)
  const amtColor = signCh === '+' ? '#7ec97e' : '#e8a0a0'
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: RV.xpCardGap,
        minHeight: RV.xpCardMinH,
        padding: RV.xpCardPad,
        boxSizing: 'border-box',
        backgroundImage: `url(${MVM_MODAL_BATTLE_REWARDS_BG_URL})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        minWidth: 0,
        width: '100%',
        maxWidth: 'none',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: RV.glyphBox,
          height: RV.glyphBox,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <XpGlyph uid={uid} variant={variant} />
      </div>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          color: TEXT_PRIMARY,
          fontSize: RV.xpLabelFont,
          fontWeight: 700,
          fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
          lineHeight: 1.2,
        }}
      >
        {label}
      </span>
      <span
        style={{
          flexShrink: 0,
          color: amtColor,
          fontSize: RV.xpAmountFont,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
        }}
      >
        {signCh}
        {formatted}
      </span>
    </div>
  )
}

export type MvMResultModalLootRow = {
  iconUrl: string | null
  name: string
  amount: number
  sign: '+' | '−' | '-'
}

function MvMLootCard({ row }: { row: MvMResultModalLootRow }) {
  const signCh = row.sign === '−' || row.sign === '-' ? '−' : '+'
  const formatted = formatCompactResourceAmount(row.amount)
  const amtColor = signCh === '+' ? '#7ec97e' : '#e8a0a0'
  const rule: React.CSSProperties = {
    width: '58%',
    maxWidth: 'min(26vw, 16dvh, 102px)',
    height: 1,
    background: 'rgba(196, 165, 116, 0.35)',
    margin: RV.lootRuleMargin,
    border: 'none',
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: RV.lootCardPad,
        boxSizing: 'border-box',
        minHeight: RV.lootCardMinH,
        width: RV.lootCardW,
        minWidth: RV.lootCardMinW,
        maxWidth: RV.lootCardW,
        flex: '0 1 auto',
        backgroundImage: `url(${MVM_MODAL_LOOT_CARD_BG_URL})`,
        backgroundSize: '100% 100%',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: RV.lootIconBox,
          height: RV.lootIconBox,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {row.iconUrl ? (
          <img
            src={row.iconUrl}
            alt=""
            draggable={false}
            style={{ width: RV.lootIconImg, height: RV.lootIconImg, objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: RV.lootFallbackGlyph, opacity: 0.45 }} aria-hidden>
            ◆
          </span>
        )}
      </div>
      <hr style={rule} />
      <span
        style={{
          color: TEXT_PRIMARY,
          fontSize: RV.lootNameFont,
          fontWeight: 700,
          fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
          lineHeight: 1.25,
          padding: '0 2px',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={row.name}
      >
        {row.name}
      </span>
      <hr style={rule} />
      <span
        style={{
          color: amtColor,
          fontSize: RV.lootAmtFont,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
        }}
      >
        {signCh}
        {formatted}
      </span>
    </div>
  )
}

export type MvMResultModalVariant = 'victory' | 'defeat'

/** Above MvMDuelArenaModal / ConfirmModal (22_000), below fog transition (99_999). */
export const MVM_RESULT_MODAL_OVERLAY_Z = 25_000

export const MvMResultModalShell: React.FC<{
  variant: MvMResultModalVariant
  overlayZIndex: number
  logoTitle: string
  description: React.ReactNode
  battleRewardsTitle: string
  showBattleRewards: boolean
  characterXp?: number
  userXp?: number
  characterXpLabel: string
  userXpLabel: string
  lootSectionTitle: string
  showLootSection: boolean
  lootRows: MvMResultModalLootRow[]
  /** Shown when `showLootSection` and there are no loot rows (e.g. no seizure). */
  lootEmptyText?: string | null
  backButtonLabel: string
  onClose: () => void
  /** Renders after the loot block (e.g. castle defeat alliance choice). */
  betweenLootAndBack?: React.ReactNode
  /** When true, the image “back to map” button is omitted (caller renders actions in `betweenLootAndBack`). */
  suppressBackButton?: boolean
}> = ({
  variant,
  overlayZIndex,
  logoTitle,
  description,
  battleRewardsTitle,
  showBattleRewards,
  characterXp = 0,
  userXp = 0,
  characterXpLabel,
  userXpLabel,
  lootSectionTitle,
  showLootSection,
  lootRows,
  lootEmptyText = null,
  backButtonLabel,
  onClose,
  betweenLootAndBack,
  suppressBackButton = false,
}) => {
  const uid = useId().replace(/:/g, '')
  const logoBg = variant === 'victory' ? MVM_MODAL_VICTORY_LOGO_BG_URL : MVM_MODAL_DEFEAT_LOGO_BG_URL
  const backBg = variant === 'victory' ? MVM_MODAL_VICTORY_BACK_BTN_URL : MVM_MODAL_DEFEAT_BACK_BTN_URL
  const titleGradient =
    variant === 'victory'
      ? 'linear-gradient(180deg, #ffffff 0%, #e8f8ea 38%, #4caf50 72%, #1b5e20 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #ffebee 38%, #ef5350 72%, #b71c1c 100%)'
  const titleFilter =
    variant === 'victory'
      ? 'drop-shadow(0 2px 3px rgba(0,0,0,.55)) drop-shadow(0 1px 0 rgba(255,255,255,.4))'
      : 'drop-shadow(0 2px 3px rgba(0,0,0,.55)) drop-shadow(0 1px 0 rgba(255,255,255,.38))'

  /**
   * Portal to `document.body` so z-index is not trapped under MvMBattleHUD (`z-[10]`);
   * otherwise portaled duel UI (22_000) and other layers can eat clicks on this overlay.
   */
  const overlay = (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: overlayZIndex,
        padding: RV.overlayPad,
        boxSizing: 'border-box',
        overflow: 'auto',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: RV.modalMaxW,
          margin: '0 auto',
          boxSizing: 'border-box',
          overflow: 'visible',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${LOGO_TOP_OFFSET_DVH}dvh`,
            height: `${LOGO_BLOCK_HEIGHT_DVH}dvh`,
            minHeight: RV.logoMinH,
            zIndex: 3,
            backgroundImage: `url(${logoBg})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: RV.logoInnerPad,
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              fontSize: RV.logoTitleFont,
              marginTop: RV.logoTitleMarginTop,
              fontWeight: 800,
              letterSpacing: '0.08em',
              lineHeight: 1.1,
              maxWidth: '100%',
              backgroundImage: titleGradient,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
              filter: titleFilter,
            }}
          >
            {logoTitle}
          </span>
        </div>

        <div
          style={{
            position: 'relative',
            zIndex: 1,
            marginTop: MODAL_PANEL_MARGIN_TOP,
            padding: RV.bodyPad,
            boxSizing: 'border-box',
            backgroundImage: `url(${MVM_MODAL_BG_URL})`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
            color: TEXT_PRIMARY,
            textAlign: 'center',
            overflow: 'visible',
          }}
        >
        <div style={{ marginTop: RV.descriptionTopMargin, marginBottom: RV.descMarginBottom }}>{description}</div>

        <img
          src={MVM_MODAL_BORDER_URL}
          alt=""
          role="presentation"
          draggable={false}
          style={{ width: '100%', height: 'auto', display: 'block', margin: `0 0 ${RV.borderMarginBottom}` }}
        />

        {showBattleRewards ? (
          <>
            <div style={sectionTitleStyle}>{battleRewardsTitle}</div>
            <div
              style={{
                paddingLeft: RV.battleRewardsPadX,
                paddingRight: RV.battleRewardsPadX,
                marginBottom: RV.blockMarginBottom,
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  alignItems: 'stretch',
                  gap: RV.battleRewardsGap,
                  width: '100%',
                }}
              >
                <MvMXpRewardCard
                  uid={uid}
                  variant="character"
                  label={characterXpLabel}
                  amount={characterXp}
                  sign="+"
                />
                <MvMXpRewardCard uid={uid} variant="account" label={userXpLabel} amount={userXp} sign="+" />
              </div>
            </div>
          </>
        ) : null}

        {showLootSection ? (
          <>
            <div
              style={{
                ...sectionTitleStyle,
                marginTop: showBattleRewards ? 'clamp(0.25dvh, 0.5dvh, 8px)' : 0,
              }}
            >
              {lootSectionTitle}
            </div>
            {lootRows.length > 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  alignItems: 'stretch',
                  justifyContent: 'center',
                  alignContent: 'center',
                  gap: RV.lootRowGap,
                  marginBottom: RV.blockMarginBottom,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  paddingBottom: RV.lootPadBottom,
                  boxSizing: 'border-box',
                }}
              >
                {lootRows.map((row, i) => (
                  <MvMLootCard key={`${row.name}-${i}`} row={row} />
                ))}
              </div>
            ) : lootEmptyText ? (
              <p
                style={{
                  margin: RV.emptyLootMargin,
                  fontSize: RV.emptyLootFont,
                  lineHeight: 1.5,
                  color: 'rgba(228,220,200,.55)',
                  fontFamily: 'system-ui, sans-serif',
                  fontWeight: 500,
                }}
              >
                {lootEmptyText}
              </p>
            ) : null}
          </>
        ) : null}

        {betweenLootAndBack}

        {!suppressBackButton ? (
          <button
            type="button"
            onClick={onClose}
            style={{
              marginTop: RV.backBtnMarginTop,
              marginLeft: 'auto',
              marginRight: 'auto',
              display: 'block',
              width: '100%',
              maxWidth: RV.backBtnMaxW,
              minHeight: RV.backBtnMinH,
              padding: RV.backBtnPad,
              border: 'none',
              cursor: 'pointer',
              backgroundImage: `url(${backBg})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundColor: 'transparent',
              color: '#f6f0e6',
              fontSize: RV.backBtnFont,
              fontWeight: 800,
              fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
              textShadow: '0 1px 3px rgba(0,0,0,.85), 0 0 10px rgba(0,0,0,.5)',
              letterSpacing: '0.06em',
            }}
          >
            {backButtonLabel}
          </button>
        ) : null}
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return null
  return createPortal(overlay, document.body)
}
